/**
 * Lade- und Schreibwege der Stammdaten. Jede Funktion bekommt den Anschluss (api) übergeben, damit dieselben
 * Wege in den Tests gegen die echte Demo-Datenbank laufen. Rechte prüft immer die Datenbank (RLS, Wächter,
 * Spaltenrechte); hier werden nur ihre Antworten in Klartext übersetzt.
 */
import { orgSettingsFromRows, type FxRateRow, type OrgConfig, type PostTermRule } from "@talentkontor/domain";
import { ApiError, emptyIfForbidden, type Api, type Filter } from "../../../data/api";
import type { AgencyTermsRow, InvoiceRow, LineItemRow, OrgSettingsRow, PeriodRow } from "../../../data/queries";
import {
  splitName, percentToBp, emailValid,
  type TeamInfo, type BookingRow, type BrandFull, type BrandPatch, type ContactRow, type ContractRow, type DealFull, type IgAccountRow, type IgAudienceRow,
  type ShareMailPreview, type IgConsentRow, type IgData, type IgDailyRow, type IgMediaRow, type IgMonthlyRow, type IgPeriodRow, type IgShareRow, type IgShareViewRow, type IgStoryRow, type InvitationRow,
  type LinkRow, type NoteRow, type PayoutRow, type PlanInfo, type PresenceRow, type RawBase, type TalentFull, type TalentPatch,
} from "./model";
import { t } from "../../../lib/sprache";
import { wirksameGrenzen } from "../../../model/tarif";

const byOrg = (orgId: string): Filter[] => [["org_id", "eq", orgId]];

// ── Klartext für Datenbank-Fehler ────────────────────────────────────────────────────────────────

/** Meldung je Constraint, deutsch und englisch (Oberflächensprache, PARITAET-8) */
const CONSTRAINTS: readonly [RegExp, [string, string]][] = [
  [/brands_org_name_uq/, ["Eine Marke mit diesem Namen gibt es in deiner Agentur schon.", "A brand with this name already exists in your agency."]],
  [/brands_org_name_legal_uq/, ["Eine Marke mit diesem Namen und diesem rechtlichen Namen gibt es schon. Einen zweiten Eintrag mit gleichem Namen unterscheidest du über den rechtlichen Namen (z. B. Lunava GmbH und Lunava AG).", "A brand with this name and this legal name already exists. You distinguish a second entry with the same name by the legal name (e.g. Lunava GmbH and Lunava AG)."]],
  [/talents_instagram_handle_check/, ["Instagram: nur Buchstaben, Ziffern, Punkt und Unterstrich, höchstens 30 Zeichen.", "Instagram: only letters, digits, dot and underscore, at most 30 characters."]],
  [/talents_tiktok_handle_check/, ["TikTok: nur Buchstaben, Ziffern, Punkt und Unterstrich, höchstens 30 Zeichen.", "TikTok: only letters, digits, dot and underscore, at most 30 characters."]],
  [/talents_youtube_handle_check/, ["YouTube: nur Buchstaben, Ziffern, Punkt, Binde- und Unterstrich.", "YouTube: only letters, digits, dot, hyphen and underscore."]],
  [/talents_whatsapp_phone_check/, ["Die WhatsApp-Nummer bitte im internationalen Format angeben, z. B. +491701234567.", "Please enter the WhatsApp number in international format, e.g. +491701234567."]],
  [/talents_nickname_check/, ["Der Rufname darf höchstens 40 Zeichen haben.", "The nickname may have at most 40 characters."]],
  [/commission_bp_check/, ["Die Provision muss zwischen 0 und 100 % liegen.", "The commission must be between 0 and 100 %."]],
  [/country_check/, ["Bitte ein Land aus der Liste wählen.", "Please choose a country from the list."]],
  [/payment_terms_days_check/, ["Das Zahlungsziel muss zwischen 0 und 180 Tagen liegen.", "The payment term must be between 0 and 180 days."]],
  [/brands_buyer_reference_check/, ["Die Käuferreferenz darf höchstens 100 Zeichen haben.", "The buyer reference may have at most 100 characters."]],
  [/brands_name_check/, ["Bitte einen Anzeigenamen angeben.", "Please enter a display name."]],
  [/brands_language_check/, ["Als Sprache sind Deutsch und Englisch möglich.", "German and English are possible as languages."]],
  [/currency_code/, ["Diese Währung wird nicht unterstützt.", "This currency is not supported."]],
  [/brand_contacts_brand_id_fkey/, ["Diese Marke gibt es nicht mehr.", "This brand no longer exists."]],
  [/talents_guardian_email_check/, ["Bitte eine gültige E-Mail-Adresse der Erziehungsberechtigten angeben.", "Please enter a valid email address of the parent or guardian."]],
  [/talents_birth_date_check/, ["Bitte ein gültiges Geburtsdatum angeben.", "Please enter a valid date of birth."]],
  [/talent_payout_details_payout_email_check/, ["Bitte eine gültige PayPal- oder Wise-Adresse angeben.", "Please enter a valid PayPal or Wise address."]],
  [/brand_contacts_roles_check/, ["Diese Zuständigkeit gibt es nicht.", "This responsibility does not exist."]],
];

function translate(e: unknown, onForeignKey?: string): unknown {
  const err = e as { message?: string; code?: string; hint?: string };
  const msg = err?.message ?? "";
  for (const [re, [de, en]] of CONSTRAINTS) if (re.test(msg)) return new ApiError(t(de, en), { code: err.code, hint: err.hint });
  if (err?.code === "23503" && onForeignKey) return new ApiError(onForeignKey, { code: err.code });
  if (err?.code === "23505") return new ApiError(t("Diesen Eintrag gibt es schon.", "This entry already exists."), { code: err.code });
  return e;
}
async function guarded<T>(fn: () => Promise<T>, onForeignKey?: string): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    throw translate(e, onForeignKey);
  }
}
function notAllowed(what: string): ApiError {
  return new ApiError(t(`${what} wurde nicht gefunden oder ist für dein Konto nicht freigegeben.`, `${what} was not found or is not available to your account.`), { code: "42501" });
}
async function one<T>(rows: Promise<T[]>, what: string): Promise<T> {
  const list = await rows;
  if (!list[0]) throw notAllowed(what);
  return list[0];
}

// ── Lesen ────────────────────────────────────────────────────────────────────────────────────────

/** Einstellungen als Fachlogik-Konfiguration (wie model/org-config.ts, aber mit übergebenem Anschluss). */
export async function fetchOrgConfig(api: Api, orgId: string): Promise<OrgConfig> {
  const [settings, policy, steps, rates, defaults] = await Promise.all([
    api.select("org_settings", { filters: byOrg(orgId) }),
    api.select("dunning_policies", { filters: byOrg(orgId) }),
    api.select("dunning_steps", { filters: byOrg(orgId), order: [["step", "asc"]] }),
    api.select("dunning_base_rates", { filters: byOrg(orgId), order: [["valid_from", "asc"]] }),
    api.select<{ key: string; value: unknown }>("platform_defaults"),
  ]);
  return orgSettingsFromRows({
    org_settings: settings[0] ?? null,
    dunning_policy: policy[0] ?? null,
    dunning_steps: steps as never,
    dunning_base_rates: rates as never,
    platform_defaults: Object.fromEntries(defaults.map((d) => [d.key, d.value])),
  });
}

export const fetchBookings = (api: Api, orgId: string) =>
  api.select<BookingRow>("bookings", { filters: byOrg(orgId), order: [["booked_on", "desc"]] });
export const fetchLinks = (api: Api, orgId: string) =>
  api.select<LinkRow>("talent_user_links", { columns: "talent_id, org_id, user_id, created_at", filters: byOrg(orgId) });
export const fetchPresence = (api: Api, orgId: string) =>
  api.select<PresenceRow>("talent_presence", { columns: "talent_id, org_id, last_seen_at, last_view, last_login_at", filters: byOrg(orgId) });
export const fetchInvitations = (api: Api, orgId: string) =>
  api.select<InvitationRow>("invitations", {
    columns: "id, org_id, kind, email, talent_id, expires_at, accepted_at, revoked_at, created_at, invited_by",
    filters: [...byOrg(orgId), ["kind", "eq", "talent"]],
    order: [["created_at", "desc"]],
  });
export const fetchContracts = (api: Api, orgId: string) =>
  api.select<ContractRow>("contracts", {
    columns: "id, org_id, deal_id, talent_id, brand_id, title, status, contract_number, sent_at, completed_at, voided_at, created_at, origin",
    filters: byOrg(orgId),
    order: [["created_at", "desc"]],
  });
export const fetchBrandContacts = (api: Api, orgId: string) =>
  api.select<ContactRow>("brand_contacts", { filters: byOrg(orgId), order: [["name", "asc"]] });

/** Alle Zeilen einer Agentur für die Stammdaten-Seiten in einem Zug: dieselben Abfragen wie die gemeinsamen Hooks
 * (data/queries.ts) plus die eigenen. Die Seiten nutzen die gemeinsamen Hooks; Tests und Werkzeuge diesen Weg. */
export async function fetchBase(api: Api, orgId: string, opts: { today: string; nowMs?: number }): Promise<RawBase> {
  const settings = (await api.select<OrgSettingsRow>("org_settings", { filters: byOrg(orgId) }))[0];
  if (!settings) throw new ApiError(t("Diese Agentur ist für dein Konto nicht freigegeben.", "This agency is not available to your account."), { code: "42501" });
  const [cfg, talents, brands, deals, periods, extras, terms, invoices, bookings, links, invitations, contracts, presence, fx] = await Promise.all([
    fetchOrgConfig(api, orgId),
    api.select<TalentFull>("talents", { filters: byOrg(orgId), order: [["first_name", "asc"]] }),
    api.select<BrandFull>("brands", { filters: byOrg(orgId), order: [["name", "asc"]] }),
    api.select<DealFull>("deals", { filters: byOrg(orgId), order: [["created_at", "desc"]] }),
    api.select<PeriodRow>("deal_periods", { filters: byOrg(orgId), order: [["month", "asc"]] }),
    api.select<LineItemRow>("deal_line_items", { filters: byOrg(orgId) }),
    emptyIfForbidden(api.select<AgencyTermsRow>("deal_agency_terms", { filters: byOrg(orgId) })),
    api.select<InvoiceRow>("invoices", { filters: byOrg(orgId), order: [["issue_date", "desc"]] }),
    fetchBookings(api, orgId),
    fetchLinks(api, orgId),
    fetchInvitations(api, orgId),
    fetchContracts(api, orgId),
    fetchPresence(api, orgId),
    // Ein Kurs je Monat wie in der Oberfläche (useFxRates): Umsätze in anderer Währung (SAAS-R2-1)
    emptyIfForbidden(api.select<FxRateRow>("fx_rates_monthly", { order: [["valid_on", "asc"]] })),
  ]);
  return {
    today: opts.today, nowMs: opts.nowMs ?? Date.now(), currency: settings.currency, settings, cfg,
    talents, brands, deals, periods, extras, terms, invoices, bookings, links, invitations, contracts, presence, fx,
  };
}

/** Tarif mit Grenzen und Modulen. Nur der Admin darf das Abo lesen; alle anderen bekommen null. */
export async function fetchPlan(api: Api, orgId: string): Promise<PlanInfo | null> {
  const sub = (await api.select<{ plan_key: string; extra_talents: number | null }>("subscriptions", { columns: "plan_key, extra_talents", filters: byOrg(orgId) }))[0];
  if (!sub) return null;
  const [plans, ents] = await Promise.all([
    api.select<{ key: string; name: string }>("plans", { columns: "key, name", filters: [["key", "eq", sub.plan_key]] }),
    api.select<{ key: string; value: number }>("plan_entitlements", { columns: "key, value", filters: [["plan_key", "eq", sub.plan_key]] }),
  ]);
  const v = (k: string) => ents.find((e) => e.key === k)?.value ?? 0;
  // 0157: gebuchte zusätzliche Creator zählen auf die Grenze (wie app.entitlement)
  const planMaxTalents = v("max_talents") >= 0 ? v("max_talents") + Math.max(0, sub.extra_talents ?? 0) : v("max_talents");
  // K-377: wirksam ist, was die Datenbank prüft (org_entitlements; im Test im vollen Umfang die Grenzen des Tarifs Agentur)
  const tarif: Record<string, number> = { max_talents: planMaxTalents, module_insights: v("module_insights"), module_whatsapp: v("module_whatsapp") };
  const { limits, fullTrial } = await wirksameGrenzen(api, orgId, tarif);
  const w = (k: string) => limits[k] ?? tarif[k] ?? 0;
  const org = fullTrial ? (await api.select<{ trial_ends_at: string | null }>("organizations", { columns: "trial_ends_at", filters: [["id", "eq", orgId]] }).catch(() => []))[0] : undefined;
  return {
    planKey: sub.plan_key, planName: plans[0]?.name ?? sub.plan_key, maxTalents: w("max_talents"), moduleInsights: w("module_insights") >= 1, moduleWhatsapp: w("module_whatsapp") >= 1,
    planMaxTalents, testBis: fullTrial ? org?.trial_ends_at ?? null : null,
  };
}

/** Gültigkeit eines Einladungslinks in Tagen: Wert der Agentur, sonst Plattform-Standard (0025). */
export async function fetchInvitationDays(api: Api, orgId: string): Promise<number> {
  const [s, d] = await Promise.all([
    api.select<{ invitation_days: number | null }>("org_settings", { columns: "invitation_days", filters: byOrg(orgId) }),
    api.select<{ value: unknown }>("platform_defaults", { columns: "value", filters: [["key", "eq", "invitation_days"]] }),
  ]);
  return s[0]?.invitation_days ?? (Number(d[0]?.value) || 14);
}

export async function fetchTalentNote(api: Api, talentId: string): Promise<NoteRow | null> {
  return (await api.select<NoteRow>("talent_internal_notes", { columns: "note, updated_at", filters: [["talent_id", "eq", talentId]] }))[0] ?? null;
}
export async function fetchBrandNote(api: Api, brandId: string): Promise<NoteRow | null> {
  return (await api.select<NoteRow>("brand_internal_notes", { columns: "note, updated_at", filters: [["brand_id", "eq", brandId]] }))[0] ?? null;
}
/** Auszahlungsdaten: die Datenbank liefert sie nur mit dem Recht „Auszahlungen ausführen“ (payout_read). */
export async function fetchPayout(api: Api, talentId: string): Promise<PayoutRow | null> {
  return (await api.select<PayoutRow>("talent_payout_details", { filters: [["talent_id", "eq", talentId]] }))[0] ?? null;
}

const SHARE_COLUMNS = "id, org_id, talent_id, brand_id, recipient_email, blocks, period, expires_at, revoked_at, failed_attempts, locked_at, sent_at, sent_count, created_by, created_at";

/** Instagram eines Creators: Konto, Kennzahlen, Einwilligung, Freigaben (ohne Prüfwert des Links, nur erlaubte Spalten). */
export async function fetchIg(api: Api, orgId: string, talentId: string): Promise<IgData> {
  const byTalent: Filter[] = [["talent_id", "eq", talentId]];
  const [account, periodTotals, audience, monthly, media, consent, shares, daily, stories] = await Promise.all([
    api.select<IgAccountRow>("ig_accounts", {
      columns: "talent_id, org_id, username, name, followers_count, media_count, status, connected_at, last_sync_at, last_error", filters: byTalent,
    }),
    api.select<IgPeriodRow>("ig_period_totals", { columns: "talent_id, period, computed_on, metrics", filters: byTalent, order: [["computed_on", "desc"]] }),
    api.select<IgAudienceRow>("ig_audience", { columns: "talent_id, captured_on, breakdown, data, timeframe", filters: byTalent, order: [["captured_on", "desc"]] }),
    api.select<IgMonthlyRow>("ig_monthly", { columns: "talent_id, month, metrics", filters: byTalent, order: [["month", "asc"]] }),
    api.select<IgMediaRow>("ig_media", { columns: "media_id, talent_id, media_type, permalink, caption, posted_at, metrics, thumbnail_path", filters: byTalent }),
    api.select<IgConsentRow>("ig_share_consent", { columns: "talent_id, org_id, sharing_allowed, allowed_blocks, updated_at", filters: byTalent }),
    api.select<IgShareRow>("ig_shares", { columns: SHARE_COLUMNS, filters: [...byOrg(orgId), ...byTalent], order: [["created_at", "desc"]] }),
    // 0340: Tageswerte für den Vergleich zum Vorzeitraum, Storys einzeln (Vorschau wie bei der Marke)
    api.select<IgDailyRow>("ig_daily", { columns: "talent_id, day, metrics", filters: byTalent, order: [["day", "desc"]], limit: 92 }),
    api.select<IgStoryRow>("ig_stories", { columns: "talent_id, story_id, media_type, posted_at, metrics, few_viewers, measured_at, measurements", filters: byTalent, order: [["posted_at", "desc"]], limit: 120 }),
  ]);
  const views = shares.length
    ? await api.select<IgShareViewRow>("ig_share_views", { columns: "id, share_id, at, ok", filters: [["share_id", "in", shares.map((s) => s.id)]] })
    : [];
  return { account: account[0] ?? null, periodTotals, audience, monthly, media, consent: consent[0] ?? null, shares, views, daily, stories };
}

export interface IgOverview { accounts: IgAccountRow[]; consents: IgConsentRow[]; shares: IgShareRow[]; views: IgShareViewRow[] }
/** Insights-Reiter (AD-193): verbundene Konten, Einwilligungen und Freigaben aller sichtbaren Creator. */
export async function fetchIgOverview(api: Api, orgId: string): Promise<IgOverview> {
  const [accounts, consents, shares, views] = await Promise.all([
    api.select<IgAccountRow>("ig_accounts", {
      columns: "talent_id, org_id, username, name, followers_count, media_count, status, connected_at, last_sync_at, last_error", filters: byOrg(orgId),
    }),
    api.select<IgConsentRow>("ig_share_consent", { columns: "talent_id, org_id, sharing_allowed, allowed_blocks, updated_at", filters: byOrg(orgId) }),
    api.select<IgShareRow>("ig_shares", { columns: SHARE_COLUMNS, filters: byOrg(orgId), order: [["created_at", "desc"]] }),
    api.select<IgShareViewRow>("ig_share_views", { columns: "id, share_id, at, ok", filters: byOrg(orgId) }),
  ]);
  return { accounts, consents, shares, views };
}

/** Frisch aus der Datenbank vor dem Bearbeiten (AD-075/AD-083: kein Überschreiben mit veralteten Werten). */
export async function fetchTalent(api: Api, talentId: string): Promise<TalentFull | null> {
  return (await api.select<TalentFull>("talents", { filters: [["id", "eq", talentId]] }))[0] ?? null;
}
export async function fetchBrand(api: Api, brandId: string): Promise<BrandFull | null> {
  return (await api.select<BrandFull>("brands", { filters: [["id", "eq", brandId]] }))[0] ?? null;
}

export const CONFLICT_TEXT = () => t("Dieser Eintrag wurde geändert. Prüfe die aktuelle Version, bevor du deine Änderungen übernimmst.", "This entry has been changed. Check the current version before applying your changes.");
/** Konflikt (Zustandsbibliothek): Hat jemand anderes den Eintrag seit dem Laden geändert, wird nicht überschrieben. */
async function assertUnchanged(api: Api, table: "talents" | "brands", id: string, expected: string | undefined) {
  if (!expected) return;
  const row = (await api.select<{ updated_at: string }>(table, { columns: "updated_at", filters: [["id", "eq", id]] }))[0];
  if (row && row.updated_at !== expected) throw new ApiError(CONFLICT_TEXT(), { code: "P0001", hint: "conflict" });
}
export function isConflict(e: unknown): boolean {
  return e instanceof ApiError && e.hint === "conflict";
}

/** Team für „Betreut von“: aktive Mitglieder mit Namen und die Zuweisungen von Creatorn an Manager. */
export async function fetchTeamInfo(api: Api, orgId: string, selfUserId: string | null): Promise<TeamInfo> {
  const [members, assignments] = await Promise.all([
    api.select<{ id: string; user_id: string; display_name: string | null; active: boolean; role: string }>("memberships", { columns: "id, user_id, display_name, active, role", filters: byOrg(orgId) }),
    api.select<{ membership_id: string; talent_id: string }>("manager_talent_assignments", { columns: "membership_id, talent_id", filters: byOrg(orgId) }),
  ]);
  const active = members.filter((m) => m.active);
  return {
    members: active.map((m) => ({ id: m.id, user_id: m.user_id, name: m.display_name?.trim() || "Ohne Namen", role: m.role })),
    assignments,
    selfId: active.find((m) => m.user_id === selfUserId)?.id ?? null,
  };
}
/** Creator einem Manager zuweisen oder die Zuweisung lösen (nur Admins, assignments_write). */
export async function setAssignment(api: Api, orgId: string, membershipId: string, talentId: string, assigned: boolean): Promise<true> {
  if (assigned) {
    await guarded(() => api.insert("manager_talent_assignments", { membership_id: membershipId, talent_id: talentId, org_id: orgId }, { columns: "membership_id" }));
  } else {
    await guarded(() => api.remove("manager_talent_assignments", [["membership_id", "eq", membershipId], ["talent_id", "eq", talentId]]));
    const still = await api.select("manager_talent_assignments", { columns: "membership_id", filters: [["membership_id", "eq", membershipId], ["talent_id", "eq", talentId]] });
    if (still.length) throw new ApiError(t("Zuweisungen ändert nur der Admin.", "Only the admin changes assignments."), { code: "42501" });
  }
  return true;
}

/** Tarifstand für alle Mitarbeiter (0031 org_entitlements): Module wie Insights sind so auch für Manager erkennbar (model/tarif.ts). */
export { fetchTarifStand as fetchEntitlements } from "../../../model/tarif";

// ── Creator schreiben ────────────────────────────────────────────────────────────────────────────

export interface Duplicate { id: string | null; name: string | null; status: string | null; reason: "email" | "name" }
/** Gleiche E-Mail (auch archiviert) oder gleicher Name in der Agentur; unsichtbare Profile ohne Namen (0042). */
export async function findTalentDuplicates(api: Api, orgId: string, input: { email?: string | null; first?: string | null; last?: string | null; exclude?: string | null }): Promise<Duplicate[]> {
  const r = await api.read<Duplicate[] | string>("talent_find_duplicates", {
    p_org: orgId, p_email: input.email?.trim() || null, p_first: input.first?.trim() || null, p_last: input.last?.trim() || null, p_exclude: input.exclude ?? null,
  });
  return (typeof r === "string" ? JSON.parse(r) : r) ?? [];
}
export async function findBrandDuplicates(api: Api, orgId: string, input: { name?: string | null; legalName?: string | null; vatId?: string | null; exclude?: string | null }): Promise<{ id: string; name: string; reason: string }[]> {
  const r = await api.read<{ id: string; name: string; reason: string }[] | string>("brand_find_duplicates", {
    p_org: orgId, p_name: input.name?.trim() || null, p_legal_name: input.legalName?.trim() || null, p_vat_id: input.vatId?.trim() || null, p_exclude: input.exclude ?? null,
  });
  return (typeof r === "string" ? JSON.parse(r) : r) ?? [];
}

/**
 * Creator anlegen über die Datenbank (talent_create, 0042): mit Dublettenprüfung; auch Manager mit zugewiesener Sicht legen an
 * und bekommen den Creator zugewiesen. Dubletten melden sich mit hint 'duplicate:talent'; allowDuplicate legt trotzdem an.
 */
export async function createTalent(api: Api, orgId: string, patch: TalentPatch, status: "active" | "invited" = "active", opts: { allowDuplicate?: boolean } = {}): Promise<TalentFull> {
  const r = await guarded(() => api.rpc<{ id: string } | string>("talent_create", { p_org: orgId, p_values: { ...patch, status }, p_allow_duplicate: !!opts.allowDuplicate }));
  const id = (typeof r === "string" ? JSON.parse(r) : r).id;
  return one(api.select<TalentFull>("talents", { filters: [["id", "eq", id]] }), t("Der Creator", "The creator"));
}
export function isDuplicate(e: unknown): boolean {
  return e instanceof ApiError && e.hint === "duplicate:talent";
}
export async function updateTalent(api: Api, talentId: string, patch: Partial<TalentPatch> & { ig_enabled?: boolean }, opts: { expectedUpdatedAt?: string } = {}): Promise<TalentFull> {
  await assertUnchanged(api, "talents", talentId, opts.expectedUpdatedAt);
  return guarded(() => one(api.update<TalentFull>("talents", [["id", "eq", talentId]], patch), t("Der Creator", "The creator")));
}
/**
 * Archivieren statt löschen (AD-078): Umsätze bleiben, der Portal-Zugang endet, offene Einladungen werden zurückgezogen
 * (sonst verbände eine angenommene Einladung ein archiviertes Profil). Reaktivieren prüft die Tarifgrenze.
 */
export async function setTalentStatus(api: Api, talentId: string, status: "active" | "archived"): Promise<TalentFull> {
  const row = await guarded(() => one(api.update<TalentFull>("talents", [["id", "eq", talentId]], { status }), t("Der Creator", "The creator")));
  if (status === "archived") {
    const open = await api.select<{ id: string }>("invitations", {
      columns: "id", filters: [["talent_id", "eq", talentId], ["accepted_at", "is", null], ["revoked_at", "is", null]],
    });
    for (const i of open) await guarded(() => api.rpc("invitation_revoke", { p_invitation: i.id }));
  }
  return row;
}
/**
 * Archivieren mit Nachlaufregel (talent_archive, 0074): Regel mit Zeitraum am Creator, laufende Kooperationen je nach Regel
 * auf den Nachlaufsatz; ohne Regel wie bisher. Offene Einladungen zieht die Oberfläche danach zurück (wie setTalentStatus).
 */
export async function archiveTalent(api: Api, talentId: string, rule: PostTermRule | null): Promise<{ until: string | null; deals_changed: number }> {
  const r = await guarded(() => api.rpc<{ until: string | null; deals_changed: number } | string>("talent_archive", { p_talent: talentId, p_post_term: rule }));
  const out = typeof r === "string" ? JSON.parse(r) : r;
  const open = await api.select<{ id: string }>("invitations", {
    columns: "id", filters: [["talent_id", "eq", talentId], ["accepted_at", "is", null], ["revoked_at", "is", null]],
  });
  for (const i of open) await guarded(() => api.rpc("invitation_revoke", { p_invitation: i.id }));
  return out;
}
/** Standard der Nachlaufregel der Agentur (Konditionen); null = keine Nachprovision vorgeschlagen. */
export async function fetchPostTermDefault(api: Api, orgId: string): Promise<PostTermRule | null> {
  const rows = await api.select<{ post_term_default: PostTermRule | null }>("org_settings", { columns: "post_term_default", filters: [["org_id", "eq", orgId]] });
  return rows[0]?.post_term_default ?? null;
}
/** Löschen nur ohne Kooperationen, Rechnungen, Verträge und Buchungen; sonst archivieren (AD-078). Nur Admins (RLS). */
export async function deleteTalent(api: Api, talentId: string): Promise<true> {
  const f: Filter[] = [["talent_id", "eq", talentId]];
  const [deals, invoices, contracts, bookings] = await Promise.all([
    api.select("deals", { columns: "id", filters: f, limit: 1 }), api.select("invoices", { columns: "id", filters: f, limit: 1 }),
    api.select("contracts", { columns: "id", filters: f, limit: 1 }), api.select("bookings", { columns: "id", filters: f, limit: 1 }),
  ]);
  if (deals.length + invoices.length + contracts.length + bookings.length > 0) {
    throw new ApiError(t("Dieser Creator hat Kooperationen, Rechnungen, Verträge oder Buchungen und kann nicht gelöscht werden. Archiviere ihn stattdessen.", "This creator has deals, invoices, contracts or bookings and cannot be deleted. Archive them instead."), { code: "P0001" });
  }
  await guarded(() => api.remove("talents", [["id", "eq", talentId]]), t("Dieser Creator hat noch verknüpfte Daten. Archiviere ihn stattdessen.", "This creator still has linked data. Archive them instead."));
  const still = await api.select("talents", { columns: "id", filters: [["id", "eq", talentId]] });
  if (still.length) throw new ApiError(t("Creator löschen darf nur der Admin.", "Only the admin may delete creators."), { code: "42501" });
  return true;
}

export interface InvitationResult { invitation_id: string; expires_at: string; token: string }
/** Einladung für einen bestehenden Creator; ältere offene Einladungen derselben Adresse zieht die Datenbank zurück (0025). */
export async function inviteTalent(api: Api, talentId: string, email?: string | null): Promise<InvitationResult> {
  return guarded(() => api.rpc<InvitationResult>("invitation_create", { p_kind: "talent", p_email: email?.trim() || null, p_talent: talentId }));
}
/**
 * Mehrere Creator auf einmal einladen (PRAXIS-8), z. B. nach dem Import: nacheinander über invitation_create, damit jede
 * Einladung ihre eigene Mail und ihren eigenen Link bekommt. Ein Fehler (Adresse schon mit anderem Profil verbunden,
 * archiviert …) hält die übrigen nicht auf; die Rückgabe nennt ihn je Creator.
 */
export async function inviteTalents(api: Api, talents: readonly { id: string; name: string }[]): Promise<{ invited: string[]; failed: { name: string; error: string }[] }> {
  const invited: string[] = [];
  const failed: { name: string; error: string }[] = [];
  for (const t of talents) {
    try {
      await inviteTalent(api, t.id);
      invited.push(t.name);
    } catch (e) {
      failed.push({ name: t.name, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return { invited, failed };
}
/** Was vor dem Archivieren noch offen ist (talent_archive_check, 0042). */
export interface ArchiveCheck { deals_open: number; contracts_pending: number; invoices_open: number; payouts_open: number; shares_active: number; invitations_open: number }
export async function fetchArchiveCheck(api: Api, talentId: string): Promise<ArchiveCheck> {
  const r = await api.read<ArchiveCheck | string>("talent_archive_check", { p_talent: talentId });
  return (typeof r === "string" ? JSON.parse(r) : r) as ArchiveCheck;
}

/** Einladen-Seite: Creator anlegen (Status „eingeladen“) und Einladung erzeugen. Leere Provision = Agentur-Standard. */
export async function inviteNewCreator(api: Api, orgId: string, input: { name: string; email: string; commission: string; language?: string | null; allowDuplicate?: boolean }): Promise<{ talent: TalentFull; invitation: InvitationResult }> {
  const { first, last } = splitName(input.name);
  if (!first) throw new ApiError(t("Bitte einen Namen angeben.", "Please enter a name."), { code: "P0001" });
  const email = input.email.trim().toLowerCase();
  if (!emailValid(email)) throw new ApiError(t("Bitte eine gültige E-Mail-Adresse angeben.", "Please enter a valid email address."), { code: "P0001" });
  const bp = percentToBp(input.commission);
  if (bp === undefined) throw new ApiError(t("Die Provision muss zwischen 0 und 100 % liegen.", "The commission must be between 0 and 100 %."), { code: "P0001" });
  const patch: TalentPatch = {
    first_name: first, last_name: last, display_name: input.name.trim().replace(/\s+/g, " "), nickname: null, email,
    instagram_handle: null, tiktok_handle: null, youtube_handle: null, whatsapp_phone: null, street: null, postal_code: null, city: null,
    country: null, vat_id: null, tax_number: null, small_business: false, commission_bp: bp, language: input.language || null,
  };
  const r = await guarded(() => api.rpc<{ id: string } | string>("talent_create", { p_org: orgId, p_values: { ...patch, status: "invited" }, p_allow_duplicate: !!input.allowDuplicate }));
  const id = (typeof r === "string" ? JSON.parse(r) : r).id as string;
  // Prüf-Chat CP-010 (02.11.2026): Profil lesen und Einladung erzeugen brauchen nur die Kennung; beides gleichzeitig statt nacheinander
  const [talent, invitation] = await Promise.all([
    one(api.select<TalentFull>("talents", { filters: [["id", "eq", id]] }), t("Der Creator", "The creator")),
    inviteTalent(api, id, email).then((x) => ({ ok: true as const, x }), (e: unknown) => ({ ok: false as const, e })),
  ]);
  if (!invitation.ok) {
    const msg = invitation.e instanceof Error ? invitation.e.message : String(invitation.e);
    throw new ApiError(t(`Der Creator ist angelegt, die Einladung ist fehlgeschlagen: ${msg}`, `The creator has been created, but the invitation failed: ${msg}`), { code: "P0001" });
  }
  return { talent, invitation: invitation.x };
}
export async function resendInvitation(api: Api, invitationId: string): Promise<InvitationResult> {
  return guarded(() => api.rpc<InvitationResult>("invitation_resend", { p_invitation: invitationId }));
}
export async function revokeInvitation(api: Api, invitationId: string): Promise<true> {
  await guarded(() => api.rpc("invitation_revoke", { p_invitation: invitationId }));
  return true;
}
/** Zugang trennen: Verknüpfung Login ↔ Creator löschen (nur Admins, talent_links_write). */
export async function removeAccess(api: Api, talentId: string): Promise<true> {
  await guarded(() => api.remove("talent_user_links", [["talent_id", "eq", talentId]]));
  const still = await api.select("talent_user_links", { columns: "talent_id", filters: [["talent_id", "eq", talentId]] });
  if (still.length) throw new ApiError(t("Den Zugang trennen darf nur der Admin.", "Only the admin may disconnect access."), { code: "42501" });
  return true;
}

async function upsertNote(api: Api, table: "talent_internal_notes" | "brand_internal_notes", key: "talent_id" | "brand_id", id: string, orgId: string, note: string): Promise<NoteRow> {
  return guarded(async () => {
    const updated = await api.update<NoteRow>(table, [[key, "eq", id]], { note });
    if (updated[0]) return updated[0];
    return one(api.insert<NoteRow>(table, { [key]: id, org_id: orgId, note }), t("Die Notiz", "The note"));
  });
}
/** Interne Notiz (nie für den Creator sichtbar). */
export const saveTalentNote = (api: Api, orgId: string, talentId: string, note: string) => upsertNote(api, "talent_internal_notes", "talent_id", talentId, orgId, note);
export const saveBrandNote = (api: Api, orgId: string, brandId: string, note: string) => upsertNote(api, "brand_internal_notes", "brand_id", brandId, orgId, note);

/** Auszahlungsdaten ändern: nur Admins (payout_write) oder der Creator selbst. */
export async function savePayout(api: Api, orgId: string, talentId: string, patch: Omit<PayoutRow, "talent_id" | "org_id" | "updated_at">): Promise<PayoutRow> {
  return guarded(async () => {
    const updated = await api.update<PayoutRow>("talent_payout_details", [["talent_id", "eq", talentId]], patch);
    if (updated[0]) return updated[0];
    return one(api.insert<PayoutRow>("talent_payout_details", { talent_id: talentId, org_id: orgId, ...patch }), t("Die Auszahlungsdaten", "The payout details"));
  });
}

// ── Insights ─────────────────────────────────────────────────────────────────────────────────────

/** Instagram-Verbindung für den Creator freischalten oder sperren (AD-194; das Tarifmodul prüft die Datenbank). */
export const setIgEnabled = (api: Api, talentId: string, enabled: boolean) => updateTalent(api, talentId, { ig_enabled: enabled });

export async function createShare(api: Api, input: { talentId: string; email: string; blocks: string[]; period: string; days: number; brandId: string | null }): Promise<{ share_id: string; token: string }> {
  return guarded(() => api.rpc<{ share_id: string; token: string }>("ig_share_create", {
    p_talent: input.talentId, p_email: input.email.trim(), p_blocks: input.blocks, p_period: input.period, p_days: input.days, p_brand: input.brandId,
  }));
}
export async function revokeShare(api: Api, shareId: string): Promise<true> {
  await guarded(() => api.rpc("ig_share_revoke", { p_share: shareId }));
  return true;
}
/** 0340: Probelauf der Mail (schreibt nichts, erzeugt keinen Link): Empfänger, Sprache, Vorlage und Werte wie bei ig_share_send. */
export async function previewShareMail(api: Api, shareId: string, message: string | null): Promise<ShareMailPreview> {
  return guarded(() => api.read<ShareMailPreview>("ig_share_send_preview", { p_share: shareId, p_message: message?.trim() || null }));
}
/** Freigabe per Mail an die Marke (Postausgang); jeder Versand erzeugt einen neuen Link, der bisherige gilt nicht mehr (0042). */
export async function sendShare(api: Api, shareId: string, message: string | null): Promise<{ token: string; sent_count: number }> {
  return guarded(() => api.rpc<{ token: string; sent_count: number }>("ig_share_send", { p_share: shareId, p_message: message?.trim() || null }));
}
/** Nach fünf Fehlversuchen gesperrte Freigabe wieder öffnen (AD-199), protokolliert. */
export async function unlockShare(api: Api, shareId: string): Promise<true> {
  await guarded(() => api.rpc("ig_share_unlock", { p_share: shareId }));
  return true;
}

// ── Marken schreiben ─────────────────────────────────────────────────────────────────────────────

export async function createBrand(api: Api, orgId: string, patch: BrandPatch): Promise<BrandFull> {
  return guarded(() => one(api.insert<BrandFull>("brands", { org_id: orgId, ...patch }), t("Die Marke", "The brand")));
}
export async function updateBrand(api: Api, brandId: string, patch: Partial<BrandPatch>, opts: { expectedUpdatedAt?: string } = {}): Promise<BrandFull> {
  await assertUnchanged(api, "brands", brandId, opts.expectedUpdatedAt);
  return guarded(() => one(api.update<BrandFull>("brands", [["id", "eq", brandId]], patch), t("Die Marke", "The brand")));
}
/** Marke löschen nur ohne Kooperationen, Rechnungen, Verträge und Buchungen (AD-086 ohne die alte Lücke). */
export async function deleteBrand(api: Api, brandId: string): Promise<true> {
  const f: Filter[] = [["brand_id", "eq", brandId]];
  const [deals, invoices, contracts, bookings] = await Promise.all([
    api.select("deals", { columns: "id", filters: f, limit: 1 }), api.select("invoices", { columns: "id", filters: f, limit: 1 }),
    api.select("contracts", { columns: "id", filters: f, limit: 1 }), api.select("bookings", { columns: "id", filters: f, limit: 1 }),
  ]);
  if (deals.length + invoices.length + contracts.length + bookings.length > 0) {
    throw new ApiError(t("Diese Marke hat Kooperationen, Rechnungen, Verträge oder Buchungen und kann nicht gelöscht werden.", "This brand has deals, invoices, contracts or bookings and cannot be deleted."), { code: "P0001" });
  }
  await guarded(() => api.remove("brands", [["id", "eq", brandId]]), t("Diese Marke hat noch verknüpfte Daten und kann nicht gelöscht werden.", "This brand still has linked data and cannot be deleted."));
  const still = await api.select("brands", { columns: "id", filters: [["id", "eq", brandId]] });
  if (still.length) throw notAllowed(t("Die Marke", "The brand"));
  return true;
}
export async function saveContact(api: Api, orgId: string, brandId: string, patch: Omit<ContactRow, "id" | "org_id" | "brand_id" | "created_at">, id?: string): Promise<ContactRow> {
  if (id) return guarded(() => one(api.update<ContactRow>("brand_contacts", [["id", "eq", id]], patch), t("Der Kontakt", "The contact")));
  return guarded(() => one(api.insert<ContactRow>("brand_contacts", { org_id: orgId, brand_id: brandId, ...patch }), t("Der Kontakt", "The contact")));
}
export async function deleteContact(api: Api, id: string): Promise<true> {
  await guarded(() => api.remove("brand_contacts", [["id", "eq", id]]));
  const still = await api.select("brand_contacts", { columns: "id", filters: [["id", "eq", id]] });
  if (still.length) throw notAllowed(t("Der Kontakt", "The contact"));
  return true;
}
